<template>
	<!-- Print layout (hidden on screen, visible only when printing) -->
	<div v-if="!isLoading" class="hidden print:block">
		<DoorsaleReceiptPrint />
	</div>

	<DoorsaleLayout
		class="print:hidden"
		:title="$t('doorsale.receipt_title')"
		:current-step="3"
		:show-back="false"
		:on-back="() => {}"
		:on-forward="() => {}"
	>
		<!-- Left column (default slot): arrival toggle + order details + new sale -->
		<div v-if="isLoading" class="flex justify-center items-center py-20 text-text">
			{{ $t('doorsale.loading_receipt') }}
		</div>
		<div v-else class="flex flex-col gap-6">
			<!-- Arrival toggle -->
			<div class="p-4 bg-surface-grey-light rounded-sm flex items-center justify-between gap-4">
				<span class="text-text">{{ $t('doorsale.mark_arrived_question') }}</span>
				<BaseToggle
					:model-value="arrivedStatus === 'success'"
					:locked="arrivedStatus === 'success'"
					:disabled="arrivedStatus === 'loading'"
					active-class="bg-success"
					@update:model-value="handleMarkArrived"
				/>
			</div>

			<!-- Order details card -->
			<div class="p-6 bg-surface-light rounded-md border border-surface-grey space-y-4">
				<h3 class="h4">{{ $t('doorsale.receipt_order_details') }}</h3>
				<div v-if="cart.receiptOrder.info?.email">
					<div class="text-sm text-text">{{ $t('doorsale.order_email') }}</div>
					<div class="font-medium">{{ cart.receiptOrder.info.email }}</div>
				</div>
				<div class="grid grid-cols-2 gap-4">
					<div>
						<div class="text-sm text-text">{{ $t('checkout.order_number') }}</div>
						<div class="font-medium">{{ cart.receiptOrder.orderid }}</div>
					</div>
					<div v-if="orderTime">
						<div class="text-sm text-text">{{ $t('checkout.order_time') }}</div>
						<div class="font-medium">{{ orderTime }}</div>
					</div>
				</div>
				<div class="flex gap-3 pt-2">
					<a
						v-if="cart.pdfLink && cart.pdfLink !== '#'"
						:href="cart.pdfLink"
						target="_blank"
						class="whitespace-nowrap inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-colors cursor-pointer px-4 py-3 text-sm bg-accent text-accent-text"
					>
						<TicketIcon class="fill-current w-6 h-6" />
						{{ $t('doorsale.fetch_tickets') }}
					</a>
					<BaseButton size="sm" color="default" @click="handlePrintReceipt">
						{{ $t('doorsale.print_receipt') }}
					</BaseButton>
				</div>
			</div>

			<!-- New sale button -->
			<BaseButton color="primary" size="lg" class="w-full gap-2" @click="handleNewSale">
				{{ $t('doorsale.new_sale') }}
				<ArrowRightIcon class="fill-current h-4 w-4" />
			</BaseButton>
		</div>

		<!-- Right column (#aside slot): receipt card -->
		<template #aside>
			<template v-if="!isLoading">
				<ReceiptBorderIcon class="fill-surface-grey-light w-full" />
				<div class="bg-surface-grey-light px-6 pb-6 break-words">
					<h2 class="h4 pt-6 pb-6">{{ $t('checkout.receipt') }}</h2>
					<div class="space-y-6">
						<CheckoutListItem v-for="item in cart.items" :key="item.id" :item="item" />
					</div>
					<div class="p-4 mt-6 bg-surface-light rounded-md border border-surface-grey">
						<CheckoutSummaryTable />
					</div>
				</div>
				<ReceiptBorderIcon class="rotate-180 fill-surface-grey-light w-full" />
			</template>
		</template>
	</DoorsaleLayout>
</template>

<script setup lang="ts">
	import { useHead } from '@unhead/vue';
	import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
	import { useI18n } from 'vue-i18n';
	import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';

	import { markAsArrived } from '@/api/doorsaleMarkArrived';
	import { fetchReceipt } from '@/api/receipt';
	import CheckoutListItem from '@/components/checkout/CheckoutListItem.vue';
	import CheckoutSummaryTable from '@/components/checkout/CheckoutSummaryTable.vue';
	import DoorsaleLayout from '@/components/doorsale/DoorsaleLayout.vue';
	import DoorsaleReceiptPrint from '@/components/doorsale/DoorsaleReceiptPrint.vue';
	import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue';
	import ReceiptBorderIcon from '@/components/icons/ReceiptBorderIcon.vue';
	import TicketIcon from '@/components/icons/TicketIcon.vue';
	import BaseButton from '@/components/ui/BaseButton.vue';
	import BaseToggle from '@/components/ui/BaseToggle.vue';
	import { useCartStore } from '@/stores/cart';
	import { useDoorsaleStore } from '@/stores/doorsaleStore';
	import { useToastStore } from '@/stores/toastStore';
	import { formatDateWithOffset, getTimestampWithOffset } from '@/utils/formatDate';
	import { useWebsocketStore } from '@/websocket/websocket';

	const { t, locale } = useI18n();
	const route = useRoute();
	const router = useRouter();
	const cart = useCartStore();
	const doorsaleStore = useDoorsaleStore();
	const toastStore = useToastStore();
	const websocketStore = useWebsocketStore();

	const isLoading = ref(true);
	const orderTime = computed(() => formatDateWithOffset(
		cart.receiptOrder.ts,
		cart.receiptOrder.ts_gmt_offset,
		{ locale: locale.value, showTime: true },
	));
	let timeoutId: ReturnType<typeof setTimeout> | null = null;

	type ArrivedStatus = 'idle' | 'loading' | 'success' | 'error';
	const arrivedStatus = ref<ArrivedStatus>('idle');

	const stopWatcher = watch(() => cart.receiptCart, (val) => {
		if (val) {
			isLoading.value = false;
			if (timeoutId) clearTimeout(timeoutId);
			stopWatcher();
		}
	});

	watch(() => cart.receiptError, (val) => {
		if (val === 'notfound') {
			router.push({ name: 'doorsale-events' });
		}
	});

	onMounted(async () => {
		// No receipt data, no active WS session, and no URL params to reload from →
		// nothing to show. Redirect without attempting a WS request that will fail.
		if (!cart.receiptCart && !cart.receiptOrder.orderid && !route.query.orderid && !websocketStore.rtoken) {
			router.push({ name: 'doorsale-events' });
			return;
		}

		if (cart.receiptCart) {
			isLoading.value = false;
			stopWatcher();
			return;
		}

		const orderid = route.query.orderid as string | undefined;
		const mailid = route.query.mailid as string | undefined;

		if (orderid && mailid) {
			// Page refresh: URL params are available, load receipt from API directly.
			const data = await fetchReceipt(orderid, mailid);
			if (data.orderid && Array.isArray(data.basket) && Number.isFinite(getTimestampWithOffset(data.ts, data.ts_gmt_offset))) {
				cart.setAPIReceipt(data);
				isLoading.value = false;
				stopWatcher();
				return;
			}
			router.push({ name: 'doorsale-events' });
			return;
		}

		// Normal doorsale flow: request the receipt over WS. The server has the full
		// order (including mailid) in memory after placeorder and responds with a
		// 'receipt' message, which triggers cart.setReceiptOrder() → receiptCart = true.
		websocketStore.send('receipt', {}, undefined);

		timeoutId = setTimeout(() => {
			if (!cart.receiptCart) {
				isLoading.value = false;
				stopWatcher();
				router.push({ name: 'doorsale-events' });
			}
		}, 15000);
	});

	async function handleMarkArrived() {
		if (!cart.receiptOrder.orderid) return;
		arrivedStatus.value = 'loading';
		try {
			const result = await markAsArrived(cart.receiptOrder.orderid);
			if (result.status === 'OK') {
				arrivedStatus.value = 'success';
			} else {
				arrivedStatus.value = 'error';
				toastStore.showToast(t('doorsale.mark_arrived_error'));
			}
		} catch {
			arrivedStatus.value = 'error';
			toastStore.showToast(t('doorsale.mark_arrived_error'));
		}
	}

	function handleNewSale() {
		router.push({ name: 'doorsale-events' });
	}

	function handlePrintReceipt() {
		window.print();
	}

	onUnmounted(() => {
		if (timeoutId) clearTimeout(timeoutId);
		stopWatcher();
		cart.resetCart();
		doorsaleStore.reset();
	});

	// Block all browser-driven back/forward navigation into the doorsale flow
	// (toolbar buttons, gestures, popstate) — design decision, not a technical constraint.
	// The "New Sale" button navigates programmatically to doorsale-events, which is allowed.
	const DOORSALE_FLOW_ROUTES = ['doorsale-tickets', 'doorsale-upsale', 'doorsale-seats', 'doorsale-information'];
	onBeforeRouteLeave(to => {
		if (DOORSALE_FLOW_ROUTES.includes(String(to.name))) return false;
	});

	useHead({
		title: t('doorsale.receipt_title'),
	});
</script>
